import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar as CalendarIcon, X, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { fmtYmd } from "@/lib/dateFormat";

/**
 * DatePickerYmd — fully custom date picker that always shows / accepts
 * `YYYY年M月D日` for human display while sending plain `YYYY-MM-DD` strings
 * to the backend. Replaces all `<input type="date">` controls so display
 * and input are unified across the app.
 *
 * Props
 *   value       string  ISO date `YYYY-MM-DD` (or empty)
 *   onChange    func    receives the ISO `YYYY-MM-DD` (or "" on clear)
 *   testid      string  data-testid for the toggle button
 *   placeholder string  default "年 / 月 / 日"
 *   required    bool    visually mark as required
 *   disabled    bool
 *   minYear / maxYear   constrain the year selector (defaults: now-30 / now+15)
 */
const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

function _parseIso(value) {
    const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return { y: parseInt(m[1]), m: parseInt(m[2]), d: parseInt(m[3]) };
}

function _toIso(y, m, d) {
    const pad = (n) => (n < 10 ? `0${n}` : String(n));
    return `${y}-${pad(m)}-${pad(d)}`;
}

function _daysInMonth(y, m) {
    return new Date(y, m, 0).getDate();
}

export default function DatePickerYmd({
    value,
    onChange,
    testid,
    placeholder = "年 / 月 / 日",
    required = false,
    disabled = false,
    minYear,
    maxYear,
    className = "",
}) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const todayParts = useMemo(() => {
        const t = new Date();
        return { y: t.getFullYear(), m: t.getMonth() + 1, d: t.getDate() };
    }, []);
    const parsed = _parseIso(value) || todayParts;
    const [viewYear, setViewYear] = useState(parsed.y);
    const [viewMonth, setViewMonth] = useState(parsed.m);

    const yMin = minYear || todayParts.y - 30;
    const yMax = maxYear || todayParts.y + 15;

    // Sync view when an outside `value` changes while picker is closed.
    useEffect(() => {
        if (!open) {
            const p = _parseIso(value) || todayParts;
            setViewYear(p.y);
            setViewMonth(p.m);
        }
    }, [value, open]);  // eslint-disable-line react-hooks/exhaustive-deps

    // Close on outside click & Escape.
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const labelText = value ? fmtYmd(value, value) : placeholder;
    const hasValue = !!_parseIso(value);

    const goPrevMonth = () => {
        if (viewMonth === 1) { setViewMonth(12); setViewYear(viewYear - 1); }
        else setViewMonth(viewMonth - 1);
    };
    const goNextMonth = () => {
        if (viewMonth === 12) { setViewMonth(1); setViewYear(viewYear + 1); }
        else setViewMonth(viewMonth + 1);
    };

    const pick = (d) => {
        onChange(_toIso(viewYear, viewMonth, d));
        setOpen(false);
    };
    const clear = () => { onChange(""); setOpen(false); };
    const pickToday = () => {
        const t = todayParts;
        setViewYear(t.y); setViewMonth(t.m);
        onChange(_toIso(t.y, t.m, t.d));
        setOpen(false);
    };

    // Build a 6-row × 7-col grid covering the visible month.
    const firstDow = new Date(viewYear, viewMonth - 1, 1).getDay();
    const dim = _daysInMonth(viewYear, viewMonth);
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= dim; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    const yearOptions = [];
    for (let y = yMax; y >= yMin; y--) yearOptions.push(y);

    const isSelected = (d) => parsed && _parseIso(value) && parsed.y === viewYear && parsed.m === viewMonth && parsed.d === d;
    const isToday = (d) => todayParts.y === viewYear && todayParts.m === viewMonth && todayParts.d === d;

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen(!open)}
                className={`prc-input !flex items-center justify-between cursor-pointer text-left ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid={testid}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <span className={hasValue ? "" : "text-[var(--prc-tertiary)]"}>{labelText}</span>
                <CalendarIcon size={16} className="text-[var(--prc-muted)] shrink-0 ml-2" />
            </button>
            {required && !hasValue && (
                // Hidden mirror so native form validation still triggers when required=true
                <input
                    type="text"
                    required
                    value=""
                    readOnly
                    onChange={() => {}}
                    aria-hidden="true"
                    tabIndex={-1}
                    style={{ position: "absolute", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
                />
            )}

            {open && (
                <div
                    className="absolute z-50 mt-1 bg-[var(--prc-surface)] border border-[var(--prc-border-strong)] rounded-sm shadow-xl p-3 w-72"
                    role="dialog"
                    data-testid={testid ? `${testid}-popover` : undefined}
                >
                    {/* Header — year select + month nav */}
                    <div className="flex items-center justify-between mb-2 gap-2">
                        <button type="button" onClick={goPrevMonth} className="prc-btn-ghost !p-1 h-7 w-7 inline-flex items-center justify-center" aria-label="前の月">
                            <CaretLeft size={16} />
                        </button>
                        <div className="flex items-center gap-1 flex-1 justify-center">
                            <select
                                value={viewYear}
                                onChange={(e) => setViewYear(parseInt(e.target.value))}
                                className="prc-input !py-0.5 !px-1.5 text-sm"
                                aria-label="年"
                            >
                                {yearOptions.map((y) => <option key={y} value={y}>{y}年</option>)}
                            </select>
                            <select
                                value={viewMonth}
                                onChange={(e) => setViewMonth(parseInt(e.target.value))}
                                className="prc-input !py-0.5 !px-1.5 text-sm"
                                aria-label="月"
                            >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                    <option key={m} value={m}>{m}月</option>
                                ))}
                            </select>
                        </div>
                        <button type="button" onClick={goNextMonth} className="prc-btn-ghost !p-1 h-7 w-7 inline-flex items-center justify-center" aria-label="次の月">
                            <CaretRight size={16} />
                        </button>
                    </div>

                    {/* Weekday header */}
                    <div className="grid grid-cols-7 text-center text-[10px] text-[var(--prc-muted)] mb-1">
                        {WEEKDAYS.map((w, i) => (
                            <span key={w} className={i === 0 ? "text-rose-300" : i === 6 ? "text-sky-300" : ""}>{w}</span>
                        ))}
                    </div>

                    {/* Day grid */}
                    <div className="grid grid-cols-7 gap-0.5">
                        {cells.map((d, idx) => {
                            if (d === null) return <span key={idx} />;
                            const sel = isSelected(d);
                            const today = isToday(d);
                            const dow = (firstDow + d - 1) % 7;
                            let cls = "h-8 text-sm rounded-sm flex items-center justify-center transition";
                            if (sel) cls += " bg-[var(--prc-green)] text-black font-bold";
                            else {
                                cls += " hover:bg-[var(--prc-surface-2)] cursor-pointer";
                                if (today) cls += " ring-1 ring-[var(--prc-green)] ring-inset";
                                if (dow === 0) cls += " text-rose-300";
                                else if (dow === 6) cls += " text-sky-300";
                            }
                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => pick(d)}
                                    className={cls}
                                    data-testid={testid ? `${testid}-day-${d}` : undefined}
                                >
                                    {d}
                                </button>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-[var(--prc-border)]">
                        <button type="button" onClick={pickToday} className="prc-btn-ghost text-xs" data-testid={testid ? `${testid}-today` : undefined}>
                            今日
                        </button>
                        <div className="flex gap-2">
                            {hasValue && (
                                <button type="button" onClick={clear} className="prc-btn-ghost text-xs inline-flex items-center gap-1 text-[var(--prc-muted)]" data-testid={testid ? `${testid}-clear` : undefined}>
                                    <X size={16} />クリア
                                </button>
                            )}
                            <button type="button" onClick={() => setOpen(false)} className="prc-btn-ghost text-xs">閉じる</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
